/**
 * ReferralCompleteDialog — "Complete referral" (the approved Dialogs artboard).
 *
 * Two questions so the loop actually closes:
 *   - Outcome: Converted / Declined / No response.
 *   - Feedback to the referrer: Given (closes the loop) / Not yet (the card
 *     keeps its FEEDBACK OWED tag and the strip nudges you).
 * Plus, only when the outcome is Converted, an OPTIONAL "Link to client
 * file": a read-only search over existing client files by client name using
 * the existing files API (getAllFiles) — nothing about a file is written.
 * And a closing note, journaled to the referral's notepad (200-char cap).
 *
 * Nothing is written here — onConfirm({ outcome, feedbackGiven,
 * linkedFileId, closingNote }) hands the answers to the detail view, which
 * calls referralsApi.completeReferral (the Step 2 service function).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { NOTE_BODY_MAX } from '../../api/referralNotesApi';
import { getAllFiles } from '../../api/filesApi';
import { REFERRAL_OUTCOMES, REFERRAL_OUTCOME_LABELS } from '../../utils/referrals';
import { formatDateZA } from '../../utils/dateHelpers';
import { DialogFrame, DialogHead, FieldLabel, Hint, OptionTile, dialogStyles as ds } from './ReferralDialogChrome';

const FILE_STATUS_LABELS = {
  submitted: 'Inbox',
  in_progress: 'In Progress',
  paused: 'Paused',
  sent_to_sign: 'Awaiting Signature',
  signed: 'Signed & Sent to New Business',
  completed: 'Completed',
};

export default function ReferralCompleteDialog({ open, referral, busy = false, onCancel, onConfirm }) {
  const [outcome, setOutcome] = useState('converted');
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [closingNote, setClosingNote] = useState('');
  const [fileQuery, setFileQuery] = useState('');
  const [linkedFile, setLinkedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesError, setFilesError] = useState(null);
  const [error, setError] = useState(null);

  // Reset on open and load the client-file list once (read-only) for the
  // Converted → "Link to client file" search.
  useEffect(() => {
    if (!open) return undefined;
    setOutcome('converted');
    setFeedbackGiven(false);
    setClosingNote('');
    setFileQuery('');
    setLinkedFile(null);
    setError(null);
    setFilesError(null);
    let cancelled = false;
    getAllFiles()
      .then((rows) => { if (!cancelled) setFiles(rows || []); })
      .catch((e) => { if (!cancelled) setFilesError(e?.message || 'Could not load client files.'); });
    return () => { cancelled = true; };
  }, [open]);

  const matches = useMemo(() => {
    const q = fileQuery.trim().toLowerCase();
    if (!q) return [];
    return files
      .filter((f) => (f.clientName || '').toLowerCase().includes(q))
      .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0))
      .slice(0, 8);
  }, [files, fileQuery]);

  const len = closingNote.length;
  const overLimit = len > NOTE_BODY_MAX;
  const counterColour =
    overLimit ? 'var(--cp-danger)' : len > NOTE_BODY_MAX * 0.75 ? 'var(--cp-referral-orange)' : 'var(--cp-text-muted)';
  const referrerName = referral?.referrer?.displayName || 'the referrer';

  const submit = () => {
    if (!REFERRAL_OUTCOMES.includes(outcome)) return setError('Choose an outcome.');
    if (overLimit) return setError(`Keep the closing note to ${NOTE_BODY_MAX} characters.`);
    setError(null);
    onConfirm &&
      onConfirm({
        outcome,
        feedbackGiven,
        linkedFileId: outcome === 'converted' && linkedFile ? linkedFile.id : null,
        closingNote: closingNote.trim(),
      });
  };

  return (
    <DialogFrame open={open} onClose={onCancel} busy={busy} width={540} label="Complete referral">
      <DialogHead referral={referral} title="Complete referral" subtitle="Two questions, so the loop actually closes." />

      <div style={ds.field}>
        <FieldLabel>Outcome</FieldLabel>
        <div style={styles.grid3}>
          {REFERRAL_OUTCOMES.map((o) => (
            <OptionTile key={o} name="ref-outcome" value={o} checked={outcome === o} onChange={setOutcome} disabled={busy}>
              {REFERRAL_OUTCOME_LABELS[o]}
            </OptionTile>
          ))}
        </div>
      </div>

      {outcome === 'converted' && (
        <div style={ds.field}>
          <FieldLabel htmlFor="ref-link-file">Link to client file</FieldLabel>
          {linkedFile ? (
            <div style={styles.linked}>
              <span>
                <b>{linkedFile.clientName}</b>
                {' — '}
                {FILE_STATUS_LABELS[linkedFile.status] || linkedFile.status}
                {linkedFile.submittedAt ? ` · submitted ${formatDateZA(linkedFile.submittedAt)}` : ''}
              </span>
              <button type="button" style={styles.clear} onClick={() => setLinkedFile(null)} disabled={busy}>
                Clear
              </button>
            </div>
          ) : (
            <>
              <input
                id="ref-link-file"
                type="text"
                value={fileQuery}
                onChange={(e) => setFileQuery(e.target.value)}
                placeholder="Search client files by name"
                style={ds.input}
                disabled={busy}
                autoComplete="off"
              />
              {matches.length > 0 && (
                <ul style={styles.results} role="listbox">
                  {matches.map((f) => (
                    <li key={f.id}>
                      <button type="button" style={styles.result} onClick={() => setLinkedFile(f)}>
                        <span style={{ fontWeight: 600 }}>{f.clientName}</span>
                        <span style={styles.resultMeta}>
                          {FILE_STATUS_LABELS[f.status] || f.status}
                          {f.submittedAt ? ` · submitted ${formatDateZA(f.submittedAt)}` : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          <Hint>
            {filesError
              ? `Client files unavailable right now (${filesError}). You can still complete without a link.`
              : "Optional. Shown only when the outcome is Converted; leave blank if the file hasn't been submitted yet."}
          </Hint>
        </div>
      )}

      <div style={ds.field}>
        <FieldLabel>Feedback to {referrerName}</FieldLabel>
        <div style={styles.grid2}>
          <OptionTile name="ref-feedback" value="given" checked={feedbackGiven} onChange={() => setFeedbackGiven(true)} small="closes the loop" disabled={busy}>
            Given
          </OptionTile>
          <OptionTile name="ref-feedback" value="not_yet" checked={!feedbackGiven} onChange={() => setFeedbackGiven(false)} small="card keeps a tag" disabled={busy}>
            Not yet
          </OptionTile>
        </div>
        {!feedbackGiven && (
          <div style={styles.owedRow}>
            <span style={styles.owedTag}>FEEDBACK OWED</span>
            <span>stays on the completed card and in the needs-attention strip until you clear it.</span>
          </div>
        )}
      </div>

      <div style={ds.field}>
        <FieldLabel htmlFor="ref-closing-note">Closing note</FieldLabel>
        <textarea
          id="ref-closing-note"
          value={closingNote}
          onChange={(e) => setClosingNote(e.target.value)}
          placeholder="e.g. Met 04/11, quotes accepted. Thank the referrer on Friday."
          rows={3}
          maxLength={NOTE_BODY_MAX + 20}
          style={{ ...ds.textarea, ...styles.noteArea }}
          disabled={busy}
        />
        <div style={{ ...ds.counter, color: counterColour }}>
          {len}/{NOTE_BODY_MAX}
        </div>
      </div>

      {error && <div style={ds.error}>{error}</div>}

      <div style={ds.row}>
        <button type="button" style={ds.secondary} onClick={onCancel} disabled={busy}>
          Back
        </button>
        <button type="button" style={ds.primary} onClick={submit} disabled={busy || overLimit}>
          {busy ? 'Completing…' : 'Complete'}
        </button>
      </div>
    </DialogFrame>
  );
}

const styles = {
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' },
  results: {
    listStyle: 'none',
    margin: '6px 0 0',
    padding: 0,
    border: '1.5px solid var(--cp-border)',
    borderRadius: '6px',
    overflow: 'hidden',
    maxHeight: '180px',
    overflowY: 'auto',
    background: 'var(--cp-surface)',
  },
  result: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '8px 12px',
    background: 'transparent',
    border: 0,
    borderBottom: '1px solid var(--cp-surface-alt)',
    textAlign: 'left',
    cursor: 'pointer',
    font: "500 13px 'Montserrat', 'DM Sans', sans-serif",
    color: 'var(--cp-text)',
  },
  resultMeta: { fontSize: '11px', color: 'var(--cp-text-muted)', whiteSpace: 'nowrap' },
  linked: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    minHeight: '40px',
    padding: '0 12px',
    border: '1.5px solid var(--cp-referral-ink)',
    borderRadius: '6px',
    background: 'var(--cp-referral-sheet)',
    fontSize: '13px',
  },
  clear: {
    background: 'transparent',
    border: 0,
    color: 'var(--cp-referral-ink)',
    fontWeight: 700,
    fontSize: '11px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  owedRow: {
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '11px',
    color: 'var(--cp-referral-label)',
  },
  owedTag: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    padding: '4px 10px',
    borderRadius: '3px',
    background: 'var(--cp-referral-orange)',
    color: 'var(--cp-surface)',
    whiteSpace: 'nowrap',
  },
  // The closing note reads as a notepad entry (cream paper, handwritten).
  noteArea: {
    background: 'var(--notepad-bg)',
    fontFamily: 'var(--font-handwritten)',
    fontSize: '19px',
    lineHeight: '26px',
    color: 'var(--notepad-text)',
  },
};
